/**
 * Same-origin JSON API client.
 *
 * In production Caddy serves this bundle and reverse-proxies `/api/*` to Spring Boot; in dev Vite
 * proxies the same prefix. Either way the browser sees one origin, so there is no CORS config and
 * the session cookie is sent automatically.
 *
 * Mutations echo the CSRF token back to the server: Spring Security writes a readable `XSRF-TOKEN`
 * cookie (`CookieCsrfTokenRepository.withHttpOnlyFalse()`) and expects it in `X-XSRF-TOKEN`. In
 * production (`app.session.secure-cookie=true`) the backend names it `__Host-XSRF-TOKEN` instead
 * (WP9 hardening) — see the `readCookie` call below.
 */
import { pushToast, reportUnauthorized } from './events';

const CSRF_COOKIE = 'XSRF-TOKEN';
const SECURE_CSRF_COOKIE = '__Host-XSRF-TOKEN';
const CSRF_HEADER = 'X-XSRF-TOKEN';

/** Shape of `ApiExceptionHandler`'s error body: `{error, message, fields}`. */
export interface ApiErrorBody {
  error?: string;
  message?: string;
  fields?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: ApiErrorBody,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The server's own `message`, when the body parsed as JSON — falls back to the generic one. */
  get serverMessage(): string {
    return this.body?.message ?? this.message;
  }

  /** Field-level validation reasons (`{"label": "must not be blank"}`), if the server sent any. */
  get fields(): Record<string, string> {
    return this.body?.fields ?? {};
  }
}

export function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Status-code handling shared by the JSON path (`request`) and the plain-text path
 * (`apiGetText`, used by the WP8 chapters/description export endpoints): rate-limit toast,
 * unauthorized broadcast, and turning any non-OK response into an `ApiError` (with its JSON body
 * when the response actually carries one). Never called for a 2xx/204 response.
 */
async function throwForFailure(method: Method, path: string, response: Response): Promise<never> {
  if (response.status === 429) {
    const retryAfter = Number(response.headers.get('Retry-After') ?? 0);
    pushToast(
      retryAfter > 0
        ? `Too many requests — try again in ${retryAfter}s.`
        : 'Too many requests — slow down for a moment.',
    );
    throw new ApiError(`${method} ${path} was rate limited`, 429);
  }

  if (response.status === 401) {
    // The session probe's own 401 is the *answer* RequireAuth is waiting for; broadcasting it
    // would make RequireAuth invalidate the very query that just failed and re-run it.
    if (path !== '/api/me') {
      reportUnauthorized();
    }
    throw new ApiError(`${method} ${path} requires a session`, 401);
  }

  let body: ApiErrorBody | undefined;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // Not every error response has a JSON body (e.g. a proxy-level 502, or a plain-text route). */
  }
  throw new ApiError(`${method} ${path} failed with ${response.status}`, response.status, body);
}

async function request<T>(method: Method, path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (method !== 'GET') {
    const token = readCookie(SECURE_CSRF_COOKIE) ?? readCookie(CSRF_COOKIE);
    if (token) {
      headers[CSRF_HEADER] = token;
    }
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (!response.ok) {
    await throwForFailure(method, path, response);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>('GET', path, signal ? { signal } : {});
}

export function apiSend<T>(method: Exclude<Method, 'GET'>, path: string, body?: unknown): Promise<T> {
  return request<T>(method, path, body !== undefined ? { body } : {});
}

/**
 * Same-origin GET returning a plain-text body (WP8's `chapters.txt`/`description.txt`), rather
 * than the JSON `request` assumes — everything else (status handling, credentials) is shared.
 */
export async function apiGetText(path: string, signal?: AbortSignal): Promise<string> {
  const response = await fetch(path, {
    method: 'GET',
    headers: { Accept: 'text/plain' },
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    await throwForFailure('GET', path, response);
  }
  return response.text();
}

export interface PingResponse {
  status: string;
  version: string;
}

export const pingQuery = {
  queryKey: ['ping'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<PingResponse>('/api/ping', signal),
};
